import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useMagazineModal } from "../components/Nav";
import { ArrowRight, ArrowDown } from "lucide-react";
import StatCountUp from "../components/StatCountUp";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export default function Home() {
  const { open } = useMagazineModal();

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen w-full overflow-hidden noise">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1610374634235-b51ef357f905?crop=entropy&cs=srgb&fm=jpg&w=2400&q=90')",
          }}
        />
        <div className="absolute inset-0 cinema-overlay" />
        <div className="absolute inset-0 bg-black/35" />

        <div className="relative z-10 min-h-screen max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 flex flex-col justify-end pb-24 md:pb-32 pt-32">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="overline mb-6"
          >
            Good Bandit · Cinematic Studio · Est. 2024
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-[14vw] md:text-[8vw] leading-[0.95] text-white"
            data-testid="hero-headline"
          >
            Stories Worth
            <br />
            <span className="italic text-white/90">Telling.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="text-white/70 text-base md:text-lg max-w-xl mt-8 leading-relaxed"
          >
            We build brands, campaigns and stories that move people and drive results.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="flex flex-col sm:flex-row gap-4 mt-12"
          >
            <Link
              to="/nocturne"
              data-testid="explore-nocturne-button"
              className="gb-btn-primary"
            >
              Explore Nocturne <ArrowRight size={14} />
            </Link>
            <Link
              to="/contact"
              data-testid="work-with-button"
              className="gb-btn-outline"
            >
              Work With Good Bandit <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>

        <div className="absolute bottom-6 right-6 md:right-12 z-20 flex items-center gap-3 text-white/40">
          <span className="kbd">SCROLL</span>
          <ArrowDown size={14} className="animate-bounce" />
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/5 py-6 overflow-hidden whitespace-nowrap bg-black">
        <div className="scroll-marquee flex gap-8 text-white/40 font-display text-2xl" data-testid="home-marquee">
          {(() => {
            const phrases = [
              "NOCTURNE",
              "BRAND FILMS",
              "CAMPAIGNS",
              "IMPACT",
              "STORYTELLING",
              "BRAND STRATEGY",
              "DIGITAL EXPERIENCES",
            ];
            // Repeat twice for seamless scroll
            const items = [...phrases, ...phrases];
            return items.flatMap((phrase, i) => [
              <span key={`p-${i}`}>{phrase}</span>,
              <span key={`s-${i}`} className="text-[var(--gb-gold)]">✳</span>,
            ]);
          })()}
        </div>
      </div>

      {/* INTRO / EDITORIAL */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
          <motion.div {...reveal} className="md:col-span-7">
            <p className="overline mb-6">Studio · Manifesto</p>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[1.05]">
              We build stories with <span className="italic text-[var(--gb-gold)]">edge</span>,
              emotion and atmosphere.
            </h2>
          </motion.div>
          <motion.div {...reveal} className="md:col-span-4 md:col-start-9 text-white/60 text-sm leading-relaxed">
            <p>
              Good Bandit is a creative studio building stories, campaigns and
              experiences that move people. From cinematic brand work to community
              driven campaigns, we combine strategy, storytelling and digital
              execution to turn ideas into action.
            </p>
            <Link to="/about" className="gb-link inline-flex items-center gap-2 mt-6 text-[var(--gb-gold)] text-xs uppercase tracking-[0.25em]">
              About the studio <ArrowRight size={12} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FEATURED WORK STRIP */}
      <section className="bg-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <motion.div {...reveal} className="flex items-end justify-between mb-10">
            <div>
              <p className="overline mb-3">Selected Work</p>
              <h3 className="font-display text-4xl md:text-5xl text-white">Featured Films</h3>
            </div>
            <Link to="/films" className="hidden md:inline-flex gb-link text-xs uppercase tracking-[0.25em] text-[var(--gb-gold)] items-center gap-2">View all <ArrowRight size={12} /></Link>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12">
          {[
            { img: "https://images.unsplash.com/photo-1623346483743-b968a27ed34c?w=1600&q=85", title: "Genesis G70 · Art of Control", cat: "Brand Film", span: "md:col-span-7" },
            { img: "https://images.unsplash.com/photo-1578245600656-e8fe67a2b5f7?w=1200&q=85", title: "Headlights / Hymns", cat: "Editorial", span: "md:col-span-5" },
            { img: "https://images.pexels.com/photos/36237587/pexels-photo-36237587.jpeg?w=1600", title: "Porsche 911 GT3 RS", cat: "Photography", span: "md:col-span-5" },
            { img: "https://images.pexels.com/photos/36427283/pexels-photo-36427283.jpeg?w=1600", title: "Vintage / Eternal", cat: "Journal", span: "md:col-span-7" },
          ].map((c, i) => (
            <motion.div key={i} {...reveal} className={`${c.span} group relative overflow-hidden h-[60vh] border-t border-l border-white/5`}>
              <div className="absolute inset-0 bg-cover bg-center transition-transform duration-[1500ms] group-hover:scale-105"
                style={{ backgroundImage: `url(${c.img})` }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
              <div className="relative z-10 h-full flex flex-col justify-end p-8">
                <p className="overline mb-3">{c.cat}</p>
                <h4 className="font-display text-3xl md:text-4xl text-white">{c.title}</h4>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* IMPACT / 03 */}
      <section className="border-t border-white/5 bg-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <motion.p {...reveal} className="overline mb-4">Impact / 03</motion.p>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
            <motion.div {...reveal} className="md:col-span-7">
              <h2 className="font-display text-5xl md:text-7xl text-white leading-[0.94]">
                Stories can move
                <br />more than people.
              </h2>
              <h3 className="font-display text-4xl md:text-6xl italic text-[var(--gb-gold)] leading-[0.98] mt-4">
                They can move communities.
              </h3>
              <p className="text-white/65 leading-relaxed mt-10 max-w-xl">
                Good Bandit helps nonprofits, community organizations and purpose led
                brands grow membership, recruit volunteers, raise funds, launch new
                programs and build stronger communities.
              </p>
              <p className="kbd text-white/40 mt-8 tracking-[0.2em]">
                CAMPAIGNS · FUNDRAISING · RECRUITMENT · MEMBERSHIP · PROGRAM DEVELOPMENT · GROWTH
              </p>
            </motion.div>
            <motion.div {...reveal} className="md:col-span-5 md:col-start-8 space-y-10">
              {[
                { big: "27,000", label: "MEMBERS ACQUIRED" },
                { big: "$7.56M", label: "ESTIMATED MEMBERSHIP VALUE" },
                { big: "$700K", label: "NATIONAL FUNDRAISING REVENUE" },
                { big: "2,000+", label: "VOLUNTEER LEADS" },
              ].map((s) => (
                <div key={s.label} className="border-b border-white/10 pb-6">
                  <p className="font-display text-6xl md:text-7xl text-white leading-[0.9]">
                    <StatCountUp value={s.big} />
                  </p>
                  <p className="kbd text-[var(--gb-gold)] mt-3">{s.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
          <motion.div {...reveal} className="mt-14">
            <Link to="/impact" className="gb-btn-outline inline-flex text-[10px]" data-testid="home-impact-cta">
              Explore Impact <ArrowRight size={12} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MAGAZINE CTA */}
      <section className="relative py-32 md:py-40 bg-black overflow-hidden noise">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1599912027667-755b68b4dd3b?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <p className="overline mb-6">Nocturne · Issue 01</p>
            <h2 className="font-display text-5xl md:text-7xl text-white leading-[1.02]" data-testid="home-magazine-cta-title">
              A cinematic editorial
              <br />
              <span className="italic text-[var(--gb-gold)]">of why we drive, not just what we drive.</span>
            </h2>
            <p className="text-white/70 text-base md:text-lg max-w-lg mt-8 leading-relaxed">
              The full Good Bandit Nocturne magazine. Film stills, photography, behind the
              drive and editorial features. Free to subscribers.
            </p>
            <button
              data-testid="home-subscribe-cta"
              onClick={() => open("home-cta")}
              className="gb-btn-primary mt-10"
            >
              Download Latest Magazine <ArrowRight size={14} />
            </button>
          </div>
          <div className="md:col-span-5 hidden md:flex items-center justify-center">
            <div className="aspect-[3/4] w-full max-w-sm relative">
              <img
                src="https://images.unsplash.com/photo-1610374634235-b51ef357f905?w=900&q=85"
                alt="Nocturne magazine cover"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-6 left-6">
                <p className="kbd text-white">ISSUE 01 · 2026</p>
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-display text-3xl text-white">Nocturne</p>
                <div className="gb-divider my-3" />
                <p className="kbd text-white/60">A GOOD BANDIT PUBLICATION</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
